import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { CompanyModel } from '@data-layer/models/companies';
import { UserModel } from '@data-layer/models/user';
import { BillItemModel, BillItemPendingModel, BillModel } from '@data-layer/models/payments';
import { enumGACheckoutSteps } from '@enum/enumGACheckoutSteps';
import { enumGATagEventLabel } from '@enum/enumGATagEventLabel';
import { enumGATagEventCategory } from '@enum/enumGATagEventCategory';
import { enumGAProductName } from '@enum/enumGAProductName';
import { enumGATagEventName } from '@enum/enumGATagEventName';
import { enumGATagEventAction } from '@enum/enumGATagEventAction';
import { CompaniesService } from './companies.service';
import { IGAProductFieldObjects } from '@interfaces/ga-product-field-objects';
import { enumCompanyPaymentType } from '@data-layer/enums/companies';
import { environment } from '@env/environment';
import { enumBillItemProductCode } from '@data-layer/enums/payments';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { GenericService } from './generic.service';
import { LoggerService } from './logger.service';
import { ConsoleLogService } from './console-log.service';
import { enumCustomEventLogName, enumCustomEventLogState } from '@app/data-layer/enums/common';
import { take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class GoogleAnalyticsService {
  constructor(
    private loggerService: LoggerService,
    private genericService: GenericService,
    private companiesService: CompaniesService,
    private translateService: TranslateService,
    private consoleLogService: ConsoleLogService,
  ) {}

  public setUser() {
    const user = this.companiesService.getUser();
    const app = this.genericService.appVersion$.getValue();

    this.gtagCustom('set', {
      userId: user.uid,
      userName: user.externalId,
      email: user?.email || '',
      companyId: user?.defaultCompanyId || '',
      organizationId: user?.defaultOrganizationId || '',
      appVersion: app?.version || '',
    });
  }

  public removeUser() {
    this.gtagCustom('set', {
      userId: undefined,
      userName: undefined,
      email: undefined,
      companyId: undefined,
      organizationId: undefined,
      appVersion: undefined,
    });
  }

  public setPageView(url: string) {
    this.gtag({
      event: enumGATagEventName.PageView,
      page: { url },
      eventLabel: enumGATagEventLabel.PageView,
    });
  }

  public viewItem(listName: string, bills: BillItemPendingModel[]) {
    const translatedListName = this.translateService.instant(listName);

    this.gtag({
      event: enumGATagEventName.ViewItem,
      ecommerce: {
        currency: DEFAULT_CURRENCY_CODE,
        value: (bills || []).reduce((prev, item) => prev + item.price * item.quantity, 0),
        items: this.getItemsFromBill(bills),
      },
      eventLabel: translatedListName,
    });
  }

  public viewProduct(listName: string, bills: BillItemPendingModel[]) {
    const translatedListName = this.translateService.instant(listName);

    this.gtag({
      event: enumGATagEventName.ViewsOfProductDetails,
      ecommerce: {
        detail: {
          list: translatedListName,
          stepTitle: translatedListName,
          items: this.getItemsFromBill(bills),
        },
      },
      eventLabel: translatedListName,
    });
  }

  public addToCart(referenceNumber: string, bills: BillItemPendingModel[]) {
    this.gtag({
      event: enumGATagEventName.AddToCart,
      ecommerce: {
        currency: DEFAULT_CURRENCY_CODE,
        value: (bills || []).reduce((prev, item) => prev + item.price * item.quantity, 0),
        items: this.getItemsFromBill(bills),
      },
      eventLabel: referenceNumber,
    });
  }

  public beginCheckout(bills: BillItemPendingModel[]) {
    const excludedProductCodes: string[] = [enumBillItemProductCode.COLLECTION_FEE];

    this.gtag({
      event: enumGATagEventName.BeginCheckout,
      ecommerce: {
        currency: DEFAULT_CURRENCY_CODE,
        value: (bills || []).reduce((prev, item) => prev + item.price * item.quantity, 0),
        items: this.getItemsFromBill(bills, excludedProductCodes),
      },
      eventLabel: enumGATagEventLabel.BeginCheckout,
    });
  }

  public purchase(bill: BillModel, transactionId: string) {
    this.loggerService.logEvent(`GA_PURCHASE_EVENT_LOGIC_START`, {
      route: location.pathname,
      transactionId,
      timestamp: new Date(),
      isShouldSend: true,
    });

    const consoleErrors: any[] = this.consoleLogService.getLogs('error');
    const companyId = this.companiesService.getCompany()?.id || '';

    this.loggerService
      .logCustomEvent({
        eventName: enumCustomEventLogName.GA_PURCHASE_PUSH_STARTED,
        state: enumCustomEventLogState.SUCCESS,
        data: {
          consoleErrors,
          customData: {
            companyId,
            transactionId,
            bill,
          },
        },
      })
      .pipe(take(1))
      .subscribe();

    const dataLayer: any[] = (window as any).dataLayer || [];
    const paymentObjectAlreadyExists = dataLayer?.some(
      (event) => event.event === enumGATagEventName.Purchase && event.ecommerce?.transaction_id === transactionId,
    );

    if (paymentObjectAlreadyExists) {
      return;
    }

    this.purchaseByBill(bill, transactionId);
  }

  public checkOutStep(bill: BillModel, step: enumGACheckoutSteps, paymentTransactionId: string) {
    this.checkOutStepByBill(bill, step, paymentTransactionId);
  }

  public companyValidate(eventAction: enumGATagEventAction, companyId: string) {
    this.gtag({
      event: enumGATagEventName.CompanyValidated,
      validation_method: eventAction,
      company_id: companyId,
    });
  }

  public setDefaultConsent() {
    this.gtagCustom('consent', 'default', {
      ad_personalization: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      analytics_storage: 'denied',
      functionality_storage: 'denied',
      personalization_storage: 'denied',
      security_storage: 'granted',
      wait_for_update: 500,
    });
  }

  public freshchatEvent(label: enumGATagEventLabel) {
    this.emitCustomEvent(enumGATagEventCategory.FreshchatWidget, enumGATagEventAction.GadgetGod, label);
  }

  public gtagCustom(...data: any) {
    if (environment.useGaTracking) {
      const dataLayer: any[] = (window as any).dataLayer || [];
      dataLayer.push(arguments);
    }
  }

  public gtag(data: any) {
    if (environment.useGaTracking) {
      const dataLayer: any[] = (window as any).dataLayer || [];
      dataLayer.push(data);
    }
  }

  public emitCustomEvent(
    eventCategory: enumGATagEventCategory,
    eventAction: enumGATagEventAction | string,
    eventLabel: enumGATagEventLabel | string,
  ) {
    this.pushEventToDataLayer(enumGATagEventName.CustomEvent, eventCategory, eventAction, eventLabel);
    if (environment.useFbTracking) {
      this.pushEventToDataLayer(enumGATagEventName.FBCustomEvent, eventCategory, eventAction, eventLabel);
    }
  }

  public pushRegistrationEvent(email: string) {
    this.pushEventToDataLayer(
      enumGATagEventName.Registration,
      enumGATagEventCategory.Auth,
      enumGATagEventAction.SignUp,
      enumGATagEventLabel.SetupBusinessProfile,
      email,
    );
    if (environment.useFbTracking) {
      this.pushEventToDataLayer(
        enumGATagEventName.FBRegistration,
        enumGATagEventCategory.Auth,
        enumGATagEventAction.SignUp,
        enumGATagEventLabel.SetupBusinessProfile,
        email,
      );
    }
  }

  /**
   * Emit google analytics event
   * Fire event example:
   * this.pushEventToDataLayer("ISF_CREATE", "ISF", "Create Isf", "User Name", 10);
   * @param {string} eventName
   * @param {string} eventCategory
   * @param {string} eventAction
   * @param {string} eventLabel
   * @param {number} eventValue
   */
  public pushEventToDataLayer(eventName: string, eventCategory: string, eventAction?: string, eventLabel?: string, eventValue?: any) {
    this.gtag({
      event: eventName,
      eventCategory,
      eventAction,
      eventLabel,
      eventValue,
    });
  }

  private getItemsFromBill(
    bills: BillItemPendingModel[] | BillModel['items'],
    extraExcludedProductCodes: string[] = [],
  ): IGAProductFieldObjects[] {
    const user = this.companiesService.getUser() || <UserModel>{};
    const company = this.companiesService.getCompany() || <CompanyModel>{};

    const excludedProductCodes: string[] = [
      enumBillItemProductCode.ES_DUTIES,
      enumBillItemProductCode.ADDITIONAL_CUSTOMS_DUTIES,
      ...extraExcludedProductCodes,
    ];

    // @ts-ignore
    return bills
      .filter((item) => !excludedProductCodes.includes(item.productCode))
      .map((item) => ({
        item_id: item?.referenceNumber || '',
        item_name: item?.description || '',
        item_brand: user.externalId,
        item_category: company.businessName,
        item_variant: company.id?.toString(),
        quantity: item.quantity,
        revenue: item.price,
        affiliation: this.getBillItemAffiliation(item),
        price: item.price,
        discount: item.discountAmount || 0,
        salesTaxAmount: item.salesTaxAmount || 0,
        cost: item.cost || 0,
      }));
  }

  private checkOutStepByBill(bill: BillModel, step: enumGACheckoutSteps, paymentTransactionId: string) {
    const itemsFromBill = this.getItemsFromBill(bill?.items || []);
    if (!itemsFromBill || !itemsFromBill.length) {
      return;
    }
    const eventLabel = {
      [enumGACheckoutSteps.PaymentDelayed]: 'Payment Delayed',
      [enumGACheckoutSteps.CreatePayment]: 'Create payment',
      [enumGACheckoutSteps.PaymentSuccess]: 'Payment success',
      [enumGACheckoutSteps.PaymentFailed]: 'Payment failed',
      [enumGACheckoutSteps.PaymentCanceled]: 'Payment canceled',
    }[step];

    this.gtag({
      event: enumGATagEventName.CheckoutSteps,
      ecommerce: {
        step: step,
        id: paymentTransactionId,
        value: (bill?.items || []).reduce((prev, item) => prev + item.price * item.quantity, 0),
        currency: DEFAULT_CURRENCY_CODE,
        tax: 0,
        shipping: 0,
        items: itemsFromBill,
      },
      eventLabel,
      paymentTransactionId,
    });
  }

  private purchaseByBill(bill: BillModel, paymentTransactionId: string) {
    const itemsFromBill: IGAProductFieldObjects[] = this.getItemsFromBill(bill?.items || []);
    if (!itemsFromBill || !itemsFromBill.length) {
      return;
    }
    const purchase = {
      transaction_id: paymentTransactionId,
      affiliation: `${itemsFromBill[0].affiliation} - ${this.translateService.instant(
        'LABELS.' + enumCompanyPaymentType[bill.providerType],
      )}`,
      value: (bill?.items || []).reduce((prev, item) => prev + item.price * item.quantity, 0),
      currency: DEFAULT_CURRENCY_CODE,
      tax: 0,
      shipping: 0,
      items: itemsFromBill,
    };

    this.loggerService.logEvent(`GA_PURCHASE_EVENT_LOGIC_MID`, {
      route: location.pathname,
      transactionId: purchase.transaction_id,
      timestamp: new Date(),
      isShouldSend: true,
    });

    const consoleErrors: any[] = this.consoleLogService.getLogs('error');
    const companyId = this.companiesService.getCompany()?.id || '';
    this.loggerService
      .logCustomEvent({
        eventName: enumCustomEventLogName.GA_PURCHASE_PUSH_PROCESSING,
        state: enumCustomEventLogState.SUCCESS,
        data: {
          consoleErrors,
          customData: {
            companyId,
            purchase,
          },
        },
      })
      .pipe(take(1))
      .subscribe();

    const eventLabel: string = bill.providerType === enumCompanyPaymentType.MONTHLY ? 'Monthly' : 'Instant';
    const gaPurchaseEcommerceEvent = {
      event: enumGATagEventName.Purchase,
      ecommerce: {
        transaction_id: paymentTransactionId,
        ...purchase,
      },
      eventLabel,
    };

    this.gtag(gaPurchaseEcommerceEvent);

    this.loggerService.logEvent(`GA_PURCHASE_EVENT_LOGIC_END`, {
      route: location.pathname,
      transactionId: paymentTransactionId,
      timestamp: new Date(),
      isShouldSend: true,
    });

    this.loggerService
      .logCustomEvent({
        eventName: enumCustomEventLogName.GA_PURCHASE_PUSH_ENDED,
        state: enumCustomEventLogState.SUCCESS,
        data: {
          consoleErrors,
          customData: {
            companyId,
            purchase,
          },
        },
      })
      .pipe(take(1))
      .subscribe();

    if (environment.useFbTracking) {
      this.gtag({
        event: enumGATagEventName.FBPurchase,
        ecommerce: {
          ...purchase,
        },
        eventLabel,
      });
    }
  }

  private getBillItemAffiliation(billItem: BillItemPendingModel | BillItemModel): enumGAProductName {
    const cbpApplicationProductCodes: string[] = [
      enumBillItemProductCode.ASSIGNED_CBP_NUMBER_REQUEST,
      enumBillItemProductCode.ASSIGNED_CBP_NUMBER_REQUEST_1,
      enumBillItemProductCode.ASSIGNED_CBP_NUMBER_REQUEST_2,
    ];

    return billItem?.referenceNumber?.startsWith('ES')
      ? enumGAProductName.ES
      : billItem?.referenceNumber?.startsWith('ISF')
      ? enumGAProductName.ISF
      : billItem?.referenceNumber?.startsWith('BR')
      ? enumGAProductName.BR
      : billItem?.referenceNumber?.startsWith('SH')
      ? enumGAProductName.LCL
      : billItem?.referenceNumber?.startsWith('MI')
      ? enumGAProductName.MI
      : cbpApplicationProductCodes.includes(billItem?.productCode)
      ? enumGAProductName.CBP
      : null;
  }
}
